// Types for SYSTEM.TABLE Print/Export functionality
// Sprint 1: Foundation - Type Definitions

import type { TableConfig, TableCell, TableColumn, TableRow, CellStyle } from './table';

// ===== Print Area Selection =====
export type PrintAreaType = 
  | 'current-table'       // All visible table data
  | 'selected-range'      // User-selected cell range
  | 'selected-columns'    // Only selected columns (all rows)
  | 'selected-rows'       // Only selected rows (all columns)
  | 'custom-selection';   // User-defined custom area

export interface PrintArea {
  type: PrintAreaType;
  
  // For 'selected-range' and 'custom-selection'
  startRow?: number;
  endRow?: number;
  startColumn?: number;
  endColumn?: number;
  
  // For 'selected-columns'
  columnIds?: string[];
  
  // For 'selected-rows'
  rowIds?: string[];
  
  // Include headers?
  includeHeaders: boolean;
  
  // Include header rows? (multi-level headers)
  includeHeaderRows: boolean;
}

// ===== Print Metadata =====
export interface PrintMetadata {
  projectName: string;
  projectDescription?: string;
  author?: string;
  dateCreated: Date;
  dateModified: Date;
  datePrinted: Date;
  version: string;
  companyName?: string;
  customFields?: Record<string, string>;
}

// ===== Print Format =====
export type PrintOutputFormat = 'pdf' | 'excel-csv';

export interface PrintFormatOptions {
  format: PrintOutputFormat;
  
  // PDF-specific options (Feature Set A - Basis Features)
  pdf?: {
    orientation: 'portrait' | 'landscape';
    pageSize: PrintPageSize;
    margins: PrintMargins;
    fontSize: number;
    fontFamily: string;
    includeGridLines: boolean;
    includeMetadataHeader: boolean;
    includePageNumbers: boolean;
    pageNumberPosition: 'bottom-center' | 'bottom-right' | 'bottom-left';
    customFieldsDisplay: 'none' | 'above-table' | 'below-table'; // NEW: Control custom fields visibility
  };
  
  // Excel/CSV-specific options
  excel?: {
    separator: ',' | ';' | '\t';
    includeFormulas: boolean;
    includeStyles: boolean;
    includeProjectMetadata: boolean; // NEW: Export project metadata in CSV
  };
}

// ===== Page Setup =====
export type PrintPageSize = 'A3' | 'A4' | 'A5' | 'Letter' | 'Legal' | 'Tabloid' | 'Auto';

export interface PrintPageDimensions {
  width: number;  // in mm
  height: number; // in mm
}

export interface PrintMargins {
  top: number;    // in mm
  right: number;  // in mm
  bottom: number; // in mm
  left: number;   // in mm
}

// ===== Print Preview =====
export interface PrintPreviewSettings {
  zoom: number; // 0.5 = 50%, 1.0 = 100%, etc.
  currentPage: number;
  totalPages: number;
  showGridLines: boolean;
  showMetadata: boolean;
}

// ===== Print Configuration (Main) =====
export interface PrintConfig {
  // Version for future migrations
  version: number;
  
  // What to print
  area: PrintArea;
  
  // Output format & options
  format: PrintFormatOptions;
  
  // Metadata
  metadata: PrintMetadata;
  
  // Preview settings
  preview: PrintPreviewSettings;
  
  // Preset information
  presetName?: string;
  isCustomPreset: boolean;
}

// ===== Print Presets =====
export interface PrintPreset {
  id: string;
  name: string;
  description?: string;
  config: Partial<PrintConfig>;
  isBuiltIn: boolean;
  createdAt: Date;
  modifiedAt: Date;
}

// ===== Print Job =====
export interface PrintJob {
  id: string;
  config: PrintConfig;
  tableConfig: TableConfig;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  createdAt: Date;
  completedAt?: Date;
  outputUrl?: string; // Blob URL for download
}

// ===== Helper Types for Export =====
export interface ExportCell {
  value: string | number | boolean;
  rowIndex: number;
  colIndex: number;
  rowSpan: number;
  colSpan: number;
  style?: CellStyle;
  isHeader: boolean;
}

export interface ExportRow {
  cells: ExportCell[];
  isHeader: boolean;
}

export interface ExportTable {
  rows: ExportRow[];
  columns: TableColumn[];
  metadata?: PrintMetadata;
}

// ===== Page Size Constants =====
export const PAGE_SIZE_DIMENSIONS: Record<PrintPageSize, PrintPageDimensions> = {
  'A3': { width: 297, height: 420 },
  'A4': { width: 210, height: 297 },
  'A5': { width: 148, height: 210 },
  'Letter': { width: 216, height: 279 },
  'Legal': { width: 216, height: 356 },
  'Tabloid': { width: 279, height: 432 },
  'Auto': { width: 0, height: 0 }, // Calculated based on content
};

// ===== Default Values =====
export const DEFAULT_PRINT_MARGINS: PrintMargins = {
  top: 20,
  right: 15,
  bottom: 20,
  left: 15,
};

export const DEFAULT_PDF_OPTIONS = {
  orientation: 'landscape' as const,
  pageSize: 'A4' as PrintPageSize,
  margins: DEFAULT_PRINT_MARGINS,
  fontSize: 10,
  fontFamily: 'helvetica',
  includeGridLines: true,
  includeMetadataHeader: true,
  includePageNumbers: true,
  pageNumberPosition: 'bottom-right' as const,
  customFieldsDisplay: 'none' as const, // NEW: Default to not display custom fields
};

export const DEFAULT_EXCEL_OPTIONS = {
  separator: ',' as const,
  includeFormulas: false,
  includeStyles: false,
  includeProjectMetadata: true, // NEW: Default to include project metadata in CSV
};

export const DEFAULT_PRINT_AREA: PrintArea = {
  type: 'current-table',
  includeHeaders: true,
  includeHeaderRows: true,
};

export const DEFAULT_PREVIEW_SETTINGS: PrintPreviewSettings = {
  zoom: 1.0,
  currentPage: 1,
  totalPages: 1,
  showGridLines: true,
  showMetadata: true,
};